import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function AdminTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get(`/admin/tickets/${id}`).then(r => {
      setTicket(r.data);
      setStatus(r.data.paymentStatus);
      setNotes(r.data.adminNotes || '');
    });
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete ticket ${ticket.ticketNumber}? This will also remove the payment receipt image and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tickets/${id}`);
      navigate('/admin/tickets');
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await api.put(`/admin/tickets/${id}/status`, { status, adminNotes: notes });
      setTicket(res.data);
      setMsg('Saved successfully');
    } catch {
      setMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckIn = async () => {
    if (!window.confirm('Mark this ticket as checked in?')) return;
    await api.post(`/admin/tickets/${id}/checkin`);
    const res = await api.get(`/admin/tickets/${id}`);
    setTicket(res.data);
  };

  if (!ticket) return <div style={{ color: 'rgba(255,255,255,0.3)', padding: 32 }}>Loading...</div>;

  const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div>
      <div className="admin-page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button className="admin-btn admin-btn-ghost" style={{ padding: '8px 16px', fontSize: 13 }} onClick={() => navigate('/admin/tickets')}>
            ← Back to List
          </button>
          <button className="admin-btn admin-btn-danger" style={{ padding: '8px 16px', fontSize: 13 }} onClick={handleDelete}>
            🗑 Delete Ticket
          </button>
        </div>
        <h1 className="admin-page-title">{ticket.ticketNumber}</h1>
        <p className="admin-page-sub">{ticket.fullName}</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
        {/* Left: Details */}
        <div>
          <div className="admin-card">
            <div className="admin-card-title">Attendee Details</div>
            <div className="admin-detail-grid">
              <div className="admin-detail-item">
                <label>Full Name</label><span>{ticket.fullName}</span>
              </div>
              <div className="admin-detail-item">
                <label>Phone Number</label><span>{ticket.phoneNumber}</span>
              </div>
              <div className="admin-detail-item">
                <label>Ticket Number</label><span>{ticket.ticketNumber}</span>
              </div>
              <div className="admin-detail-item">
                <label>Coupon Code</label><span>{ticket.couponCode || '—'}</span>
              </div>
              <div className="admin-detail-item">
                <label>Free Ticket</label><span>{ticket.isFreeTicket ? 'Yes' : 'No'}</span>
              </div>
              <div className="admin-detail-item">
                <label>Submitted</label><span>{fmt(ticket.createdAt)}</span>
              </div>
              <div className="admin-detail-item">
                <label>Checked In</label>
                <span>
                  {ticket.checkedIn
                    ? `✓ ${fmt(ticket.checkedInAt)}`
                    : <button className="admin-btn" style={{ padding: '6px 14px', fontSize: 13 }} onClick={handleCheckIn}>Mark Checked In</button>}
                </span>
              </div>
            </div>
          </div>

          {/* Receipt */}
          {ticket.receiptImage && (
            <div className="admin-card">
              <div className="admin-card-title">Payment Receipt</div>
              <img
                src={`/uploads/receipts/${ticket.receiptImage}`}
                alt="Receipt"
                style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div>
          <div className="admin-card">
            <div className="admin-card-title">Payment Status</div>

            <div className="admin-radio-group" style={{ flexDirection: 'column' }}>
              {['approved', 'declined', 'pending'].map(s => (
                <label key={s} className={`admin-radio-card ${s} ${status === s ? 'selected' : ''}`} onClick={() => setStatus(s)}>
                  <input type="radio" name="status" value={s} checked={status === s} onChange={() => setStatus(s)} />
                  <div>
                    <div className="admin-radio-label" style={{ textTransform: 'capitalize' }}>
                      {s === 'approved' ? '✅ ' : s === 'declined' ? '❌ ' : '⏳ '}{s}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                      {s === 'approved' ? 'Payment received & verified'
                        : s === 'declined' ? 'Payment not received / invalid'
                        : 'Awaiting payment verification'}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="admin-field">
              <label className="admin-label">Admin Notes</label>
              <textarea
                className="admin-input" rows={3} placeholder="Optional notes..."
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            <button className="admin-btn" style={{ width: '100%', padding: 14 }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {msg && <p style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: msg.includes('fail') ? '#f87171' : '#4ade80' }}>{msg}</p>}
          </div>

          {/* QR Preview */}
          {ticket.qrCodeData && (
            <div className="admin-card" style={{ textAlign: 'center' }}>
              <div className="admin-card-title">Ticket QR Code</div>
              <img src={ticket.qrCodeData} alt="QR" style={{ width: 180, height: 180, border: '8px solid #fff', borderRadius: 12 }} />
              <p style={{ marginTop: 10, fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>{ticket.ticketNumber}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
